"use client";

import * as React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { icons } from "../../assets/icons";
import { confirmarAccesoFinal, type Apartamento } from "../../api/authService";
import { useAuth } from "../../context/AuthContext";

export default function ApartmentSelectionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginFinalize } = useAuth();

  const {
    email,
    password,
    usuario,
    unidadId,
    unidadNombre,
    rolId,
    rolNombre,
    apartamentos,
    roles,
    unidades,
  } = location.state || {
    email: "",
    password: "",
    usuario: null,
    unidadId: "",
    unidadNombre: "Unidad seleccionada",
    rolId: "",
    rolNombre: "Rol seleccionado",
    apartamentos: [] as Apartamento[],
    roles: [],
    unidades: [],
  };

  const [loadingId, setLoadingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const onSelect = async (aptId: string, aptNombre: string) => {
    try {
      setError(null);
      setLoadingId(aptId);

      // Autenticacion_4 → confirmación final
      await confirmarAccesoFinal({
        email,
        contrasena: password,
        CodigoUnidadResidencial: unidadId,
        TipoUsuario: rolId,
        CodigoApartamento: aptId,
      });

      // Guardar la sesión completa
      await loginFinalize({
        email,
        usuario, // Incluir usuario
        unidad: { id: unidadId, nombre: unidadNombre },
        rol: { id: rolId, nombre: rolNombre },
        apartamento: { id: aptId, nombre: aptNombre },
        // token: se podría agregar si el backend lo emite
      });

      navigate("/dashboard", {
        state: {
          unit: unidadNombre,
          role: rolNombre,
          apartment: aptNombre,
        },
      });
    } catch (e: any) {
      setError(e?.message || "No fue posible confirmar el acceso.");
    } finally {
      setLoadingId(null);
    }
  };

  const handleGoBack = () => {
    navigate("/RoleSelectionPage", {
      state: {
        email,
        password,
        usuario, // Pasar usuario
        unidadId,
        unidadNombre,
        roles, // Pasar los roles originales
        unidades, // Pasar unidades para la cadena completa de navegación
      },
    });
  };

  // Si no hay datos de navegación válidos, redirigir al login
  React.useEffect(() => {
    if (!email || !password || !Array.isArray(apartamentos) || apartamentos.length === 0) {
      console.log("Datos inválidos en ApartmentSelection, redirigiendo al login:", { 
        email, password, apartamentos 
      });
      navigate("/", { replace: true });
    }
  }, [email, password, apartamentos, navigate]);

  // Mostrar loading si aún no hay datos
  if (!email || !password || !Array.isArray(apartamentos)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 px-4">
      <Card
        className="w-full max-w-lg shadow-xl border border-gray-200 rounded-2xl"
        style={{ backgroundColor: "rgb(247,247,247)" }}
      >
        <CardHeader>
          <div className="flex flex-col items-center text-center">
            <img src={icons.logo} alt="EasyPH Logo" className="w-60 h-auto" />
            <Button
              onClick={handleGoBack}
              className="mt-2 w-full text-white transition-all duration-200 shadow-md font-medium py-2 px-4 rounded-lg"
              style={{
                backgroundColor: "#DF8037",
                borderColor: "#DF8037",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#E55A00";
                e.currentTarget.style.borderColor = "#E55A00";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#DF8037";
                e.currentTarget.style.borderColor = "#DF8037";
              }}
            >
              Regresar a Roles
            </Button>
            <CardTitle className="text-2xl font-bold text-military-600 mt-3">
              {email}
            </CardTitle>
            <p className="text-base font-medium text-military-600 mt-1">
              {unidadNombre}
            </p>
            <p className="text-sm text-gray-600 mt-1">{rolNombre}</p>
            <p className="text-base font-medium text-gray-600 mt-2">
              Selecciona tu apartamento
            </p>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {apartamentos.map((apartment: Apartamento) => (
              <Button
                key={apartment.id}
                onClick={() => onSelect(apartment.id, apartment.nombre)}
                disabled={loadingId === apartment.id}
                className="w-full bg-military-500 hover:bg-military-600 text-white shadow-md rounded-lg disabled:opacity-50"
              >
                {loadingId === apartment.id ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Confirmando...
                  </>
                ) : (
                  apartment.nombre
                )}
              </Button>
            ))}

            {apartamentos.length === 0 && (
              <p className="text-center text-gray-500 text-sm">
                No se encontraron apartamentos disponibles
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}